import {
  Container,
  ContentLayout,
  Header,
} from '@cloudscape-design/components';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import Input from '../../../components/Input';
import Layout from '../../../components/Layout';
import {
  UpdateWebsiteSectionInput,
  useGetWebsiteSectionsQuery,
  useUpdateWebsiteSectionMutation,
  WebsiteSection,
  WebsiteSectionKeyEnum,
} from '../../../generated/graphql';
import useNotifications from '../../../hooks/useNotifications';
import { ErrorText } from '../../../lib/ErrorTexts';
import MetaTags from '../../../lib/MetaTags';

type FormValues = {
  id: string;
  key: string;
  title: string;
  subtitle?: string;
};

const schema = yup
  .object()
  .shape({
    title: yup.string().required(ErrorText.Required),
    id: yup.string().required(ErrorText.Required),
    key: yup.string().required(ErrorText.Required),
  })
  .required();

const About = () => {
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {},
    resolver: yupResolver(schema),
  });

  const { data: getData } = useGetWebsiteSectionsQuery();
  const [updateWebsiteSection, { data: updateData, loading: updateLoading }] =
    useUpdateWebsiteSectionMutation();

  useEffect(() => {
    let data: WebsiteSection;
    if (
      getData &&
      getData.listWebsiteSections &&
      getData.listWebsiteSections.items
    ) {
      data = getData.listWebsiteSections.items.filter(
        (item) => item?.key === WebsiteSectionKeyEnum.WebsiteSectionAbout,
      )[0] as WebsiteSection;
      if (updateData && updateData.updateWebsiteSection)
        data = updateData.updateWebsiteSection;
      if (data) {
        methods.reset({
          id: data.id ?? '-',
          key: data.key ?? '-',
          title: data.title ?? '-',
          subtitle: data.subtitle ?? '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData, methods.reset]);

  const {
    notifications,
    notifyDeletedOrUpdated,
    notifyFailed,
    notifyInProgress,
  } = useNotifications();

  const onSubmitForm = (data: FormValues) => {
    const messageId = uuidv4();
    notifyInProgress({
      id: messageId,
      header: 'Eintrag wird gespeichert...',
    });
    //Update entry
    updateWebsiteSection({
      variables: { input: data as UpdateWebsiteSectionInput },
    })
      .then((data) => {
        if (data.data?.updateWebsiteSection) {
          notifyInProgress();
          notifyDeletedOrUpdated({
            id: messageId,
            type: 'success',
            header: 'Gespeichert',
            dismissible: true,
          });
        }
      })
      .catch((error) => {
        notifyInProgress();
        notifyFailed({
          id: messageId,
          content: JSON.stringify(error),
          header: 'Ein Fehler ist aufgetreten',
          dismissible: true,
        });
      });
  };

  const disableSubmitButton = useMemo(() => {
    if (updateLoading) return true;
    if (isEmpty(methods.formState.dirtyFields)) return true;
    return false;
  }, [methods.formState, updateLoading]);

  return (
    <Layout
      notifications={notifications}
      breadcrumbs={[
        { text: 'Webseite', href: '/website' },
        { text: "Über Theo's", href: '/website/about' },
      ]}
    >
      <MetaTags title="Über Theo's | Theo's Bistro" />
      <ContentLayout header={<Header variant="h1">Über Theo's</Header>}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmitForm)}>
            <Container header={<Header variant="h2">Einstellungen</Header>}>
              <SpaceBetween direction="vertical" size="m">
                <Input name="title" label="Titel" stretch />
                <Input name="subtitle" label="Untertitel" stretch />
              </SpaceBetween>
            </Container>
            <div className="flex w-full gap-4 justify-end mt-4">
              <Button formAction="none" onClick={() => methods.reset()}>
                Zurücksetzen
              </Button>
              <Button
                variant="primary"
                disabled={disableSubmitButton}
                formAction="submit"
              >
                Speichern
              </Button>
            </div>
          </form>
        </FormProvider>
      </ContentLayout>
    </Layout>
  );
};

export default About;
